"use strict";
/**
 * @file format.ts
 *
 * Locale-aware CLDR formatting helpers built on the platform's native `Intl`
 * APIs (Node 24 and modern browsers ship full ICU, so no CLDR data bundles are
 * needed). These cover formatting outside of translated messages; message
 * interpolation goes through the ICU runtime in {@link ./intl}.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatMoney = formatMoney;
exports.formatNumber = formatNumber;
exports.formatDate = formatDate;
exports.formatRelativeTime = formatRelativeTime;
/**
 * Formats a {@link Money} value as a localized currency string. The amount is
 * interpreted as minor units (e.g. cents) and divided by 100, so
 * `{ amount: 12345, currency: 'EUR' }` renders as `123,45 €` in `de-DE` and
 * `€123.45` in `en-GB`.
 *
 * @param money - The amount in minor units and its ISO-4217 currency code.
 * @param locale - The BCP-47 locale to format for.
 * @param options - Optional `Intl.NumberFormat` overrides.
 *
 * @returns The amount rendered as a localized currency string.
 */
function formatMoney({ amount, currency }, locale, options) {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        ...options,
    }).format(amount / 100);
}
/**
 * Formats a number for the given locale.
 *
 * @param value - The number to format.
 * @param locale - The BCP-47 locale to format for.
 * @param options - Optional `Intl.NumberFormat` overrides.
 *
 * @returns The localized number string.
 */
function formatNumber(value, locale, options) {
    return new Intl.NumberFormat(locale, options).format(value);
}
/**
 * Formats a date or timestamp for the given locale.
 *
 * @param date - The `Date` (or epoch milliseconds) to format.
 * @param locale - The BCP-47 locale to format for.
 * @param options - Optional `Intl.DateTimeFormat` overrides.
 *
 * @returns The localized date string.
 */
function formatDate(date, locale, options) {
    return new Intl.DateTimeFormat(locale, options).format(date);
}
/**
 * Formats a relative time (e.g. "in 3 days", "2 hours ago") for the given locale.
 *
 * @param value - The signed amount of `unit` (negative is in the past).
 * @param unit - The relative-time unit, e.g. `'day'`.
 * @param locale - The BCP-47 locale to format for.
 * @param options - Optional `Intl.RelativeTimeFormat` overrides.
 *
 * @returns The localized relative-time string.
 */
function formatRelativeTime(value, unit, locale, options) {
    return new Intl.RelativeTimeFormat(locale, options).format(value, unit);
}
